'use client'

import { MouseEventHandler, ReactNode, useEffect, useRef, useState } from 'react'

import Image from 'next/image'

import Dialog from './dialog'
import InfoSVG from './info-icon.svg'

import styles from './tooltip.module.scss'

type TooltipProps = {
  /**
   * Just the icon to hover
   * The default is the info icon
   */
  children?: ReactNode
  /**
   * The dialog's content
   */
  text: string
  /**
   * Class used just for positioning the tooltip icon.
   */
  className?: string
  /**
   * The aria-describedby attribute lists the ids of the elements that describe the object.
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-describedby
   */
  describedBy: string
}

export default function Tooltip({ children, text, className, describedBy }: TooltipProps): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const ref = useRef<HTMLDivElement>(null)
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    const stopObserving = (): void => {
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect()
        intersectionObserverRef.current = null
      }
    }

    const handleInterception: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          setIsOpen(false)
          stopObserving()
        }
      })
    }

    const observeComponent = (): void => {
      const element = ref?.current
      if (element) {
        intersectionObserverRef.current = new IntersectionObserver(handleInterception)
        intersectionObserverRef.current.observe(element)
      }
    }

    if (isOpen) {
      observeComponent()
    }

    return (): void => {
      stopObserving()
    }
  }, [isOpen])

  const onMouseEnter: MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsOpen(true)
  }

  // Tooltip accessibility doc: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/tooltip_role
  return (
    <div className={[styles.tooltip, className].filter(Boolean).join(' ')} aria-describedby={describedBy} ref={ref}>
      <div
        className={styles.icon}
        onMouseEnter={onMouseEnter}
        onClick={onMouseEnter}
        onMouseLeave={(): void => setIsOpen(false)}
      >
        {children || <Image src={InfoSVG} alt="info" className={styles['info-icon']} />}
      </div>
      {isOpen && <Dialog text={text} />}
    </div>
  )
}
